import { OrderResponse } from "../../../../hooks/services/useFetchOrderQuery";
import React, { useCallback, useEffect, useMemo } from "react";
import useTimer from "../../../../hooks/time/useTimer";
import { motion } from "framer-motion";
import BrandLogo from "app/components/ui/BrandLogo/BrandLogo";
import TermsLink from "app/components/ui/BrandLogo/TermsLink";

export interface IPaymentResolutionPage {
  order?: OrderResponse;
}

const REDIRECT_DELAY = 10_000;

const PaymentCanceled: React.FC<IPaymentResolutionPage> = ({ order }) => {
  const redirectAt = useMemo(() => Date.now() + REDIRECT_DELAY, []);
  const { timeLeft, finished } = useTimer(redirectAt);

  const redirect = useCallback(() => {
    if (!order?.additionalInfo?.redirectUrl?.cancel) return;
    sessionStorage.clear();
    window.location.href = encodeURI(order.additionalInfo.redirectUrl.cancel);
  }, [order]);

  useEffect(() => {
    if (finished) redirect();
  }, [finished, redirect]);

  const secondsLeft = Math.max(0, Math.ceil((timeLeft || 0) / 1000));
  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  return (
    <>
      <div className="w-full max-w-lg mx-auto bg-white rounded-40 shadow-xl overflow-hidden">
        <div className="p-[20px] md:p-[40px] text-center flex flex-col items-center rounded-40">
        <div className="padding-24 bg-[#F9F7FD] rounded-20">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center margin-bottom-32"
            >
              <img src="/warning-icon.svg" alt="" />
            </motion.div>

            <h2 className="text-s2 text-primary font-medium mb-3">Payment Cancelled</h2>
            <p className="text-s5 text-gray-600 margin-bottom-36">
              This payment was cancelled. No funds were transferred.
            </p>

            {order?.additionalInfo?.redirectUrl?.cancel && (
              <div className="rounded-xl bg-white border border-[#ECEEF2] padding-18 md:mx-6 text-primary font-normal">
                <div className="flex items-start justify-center spacing-4 mb-4">
                  <svg className="width-16 height-16 animate-spin " fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-s5 font-normal leading-normal md:leading-none">
                    Redirecting you to seller&apos;s site in... {minutes}:{seconds}
                  </span>
                </div>
                <p className="text-s5 font-normal">
                  If you&apos;re not redirected automatically, please click{" "}
                  <button type="button" onClick={redirect} className="font-medium underline">
                    here
                  </button>
                  .
                </p>
              </div>
            )}
          </div>
          {/* Footer */}
          <div className="hidden md:block text-center text-s6 text-gray-400 padding-top-40">
            <div className="flex flex-row items-center justify-center spacing-4">
              <div className="padding-bottom-4 text-primary text-s5 font-normal">Powered by</div>
              <BrandLogo className="h-[20px] w-auto max-w-[120px] object-contain" />
            </div>
            By using this payment gateway, you agree to the{" "}
            <TermsLink className="underline" />
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="block md:hidden text-center text-s6 text-gray-400 padding-top-40">
        <div className="flex flex-row items-center justify-center spacing-4">
          <div className="padding-bottom-4 text-primary text-s5 font-normal">Powered by</div>
          <BrandLogo className="h-[20px] w-auto max-w-[120px] object-contain" />
        </div>
        By using this payment gateway, you agree to the{" "}
        <TermsLink className="underline" />
      </div>
    </>
  );
};

export default PaymentCanceled;
